import { modal_html } from './modal'
import { APP_BRAND_MARK_SRC, APP_BRAND_NAME } from '../lib/brand'
import { fetch_config } from '../lib/config'
import {
  can_offer_installation,
  is_direct_native_app_access,
  is_samsung_internet_webapk_blocked,
  normalize_app_store_links,
  prefers_native_app_store,
  preferred_app_store,
  should_defer_to_ios_smart_app_banner,
  visible_app_stores,
  type AppStoreLink,
  type AppStoreLinks,
} from '../lib/app-install'
import { is_driver_flow_path } from '../lib/driver-paths'
import { is_rede_mode, rede_display_name } from '../lib/rede-context'
import { store } from '../lib/store'
import { escapeHtml } from '../lib/utils'
import { browser_local_storage } from '../lib/browser-storage'
import {
  consume_pending_pwa_shortcut_install_help,
  is_pwa_standalone,
  prepare_pwa_shortcut_install,
  restore_pwa_shortcut_install_url,
} from '../lib/pwa-shortcut'

// Evento não-padrão do Chrome/Edge para instalação de PWA (ausente na lib DOM).
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

let deferred_prompt: BeforeInstallPromptEvent | null = null
let banner_el: HTMLElement | null = null
let app_store_links: AppStoreLinks = normalize_app_store_links({})
let app_store_links_loading = false

const DISMISS_KEY = 'fidelidade_install_dismissed_v2'
const OFFERS_SHORTCUT_TITLE = 'Na Bomba'

type BannerBrand = { name: string; logo: string }
type BannerKind = 'pwa' | 'native-app'

function is_standalone(): boolean {
  return is_pwa_standalone()
}

function is_ios_mobile(): boolean {
  const ua = navigator.userAgent || ''
  return /iPhone|iPad|iPod/i.test(ua)
}

function is_android(): boolean {
  return /Android/i.test(navigator.userAgent || '')
}

function is_pwa_install_blocked(): boolean {
  return is_samsung_internet_webapk_blocked(navigator.userAgent || '')
}

function native_app_offer_allowed(): boolean {
  return is_direct_native_app_access(window.location.pathname, window.location.search, is_rede_mode())
}

function preferred_native_app_store(): AppStoreLink | null {
  return preferred_app_store(navigator.userAgent || '', app_store_links)
}

function visible_native_app_stores(): AppStoreLink[] {
  if (!native_app_offer_allowed()) return []
  return visible_app_stores(navigator.userAgent || '', app_store_links)
}

function banner_kind(): BannerKind {
  return native_app_offer_allowed() && prefers_native_app_store(navigator.userAgent || '') ? 'native-app' : 'pwa'
}

function is_whatsapp_webview(): boolean {
  return /WhatsApp/i.test(navigator.userAgent || '')
}

function has_manual_install_flow(): boolean {
  return is_ios_mobile() || is_android()
}

function has_install_session(): boolean {
  return !!store.get('phone') || !!store.get('driver_phone')
}

function was_dismissed(): boolean {
  const ts = browser_local_storage.getItem(DISMISS_KEY)
  if (!ts) return false
  const elapsed = Date.now() - Number(ts)
  return elapsed < 7 * 24 * 60 * 60 * 1000
}

function banner_copy(kind: BannerKind): { button: string; subtitle: string } {
  if (kind === 'native-app') {
    return {
      button: preferred_native_app_store()?.name || 'Baixar app',
      subtitle: 'Abra ou baixe o app oficial',
    }
  }
  if (deferred_prompt) {
    return {
      button: 'Instalar',
      subtitle: 'Adicione rapido pela tela inicial',
    }
  }
  return {
    button: 'Criar atalho',
    subtitle: 'Salvar com os filtros atuais deste aparelho',
  }
}

function app_store_link_html(link: AppStoreLink, primary = false): string {
  const label = `Baixar na ${link.name}`
  return `
    <a href="${escapeHtml(link.url)}"
       class="btn ${primary ? 'btn-primary install-banner-store-primary' : 'btn-outline-secondary install-banner-store-icon'} btn-sm"
       aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}" target="_blank" rel="noopener noreferrer">
      <i class="${link.icon_class}" aria-hidden="true"></i>${primary ? `<span>${escapeHtml(link.name)}</span>` : ''}
    </a>`
}

function load_app_store_links(): void {
  if (app_store_links_loading) return
  app_store_links_loading = true
  void fetch_config()
    .then(config => {
      app_store_links = normalize_app_store_links(config)
      sync_install_banner()
    })
    .catch(() => {})
}

function brand_initial(value: string): string {
  return String(value || 'R').trim().slice(0, 1).toUpperCase() || 'R'
}

function banner_brand(): BannerBrand {
  if (is_rede_mode()) {
    return {
      name: rede_display_name(),
      logo: String(store.get('rede_logo') || '').trim(),
    }
  }

  if (is_driver_flow_path(window.location.pathname)) {
    return {
      name: OFFERS_SHORTCUT_TITLE,
      logo: APP_BRAND_MARK_SRC,
    }
  }
  const has_client_session = !!store.get('phone')
  const station_name = String(store.get('station_name') || '').trim()
  const station_logo = String(store.get('station_logo') || '').trim()
  if (has_client_session && station_name) {
    return {
      name: station_name,
      logo: station_logo || APP_BRAND_MARK_SRC,
    }
  }
  return {
    name: APP_BRAND_NAME,
    logo: APP_BRAND_MARK_SRC,
  }
}

function banner_brand_media(brand: BannerBrand): string {
  const safe_name = escapeHtml(brand.name)
  if (brand.logo) {
    return `<img src="${escapeHtml(brand.logo)}" alt="${safe_name}" width="40" height="40" class="install-banner-logo rounded-2">`
  }

  return `<span class="install-banner-logo install-banner-logo--initial" aria-hidden="true">${escapeHtml(brand_initial(brand.name))}</span>`
}

function bind_banner_events(): void {
  document.getElementById('install-btn')?.addEventListener('click', () => { void open_install_flow() })
  document.getElementById('install-dismiss')?.addEventListener('click', () => {
    browser_local_storage.setItem(DISMISS_KEY, String(Date.now()))
    remove_banner()
  })
}

function show_install_help(): void {
  const ios_steps = `
    <div class="small text-muted mb-3">No iPhone, o atalho abre com o contexto atual do app.</div>
    <ol class="ps-3 mb-0">
      <li class="mb-2">Toque em <strong>Compartilhar</strong> no navegador.</li>
      <li class="mb-2">Escolha <strong>Adicionar a Tela de Inicio</strong>.</li>
      <li>Confirme em <strong>Adicionar</strong>.</li>
    </ol>
    ${is_whatsapp_webview() ? `
      <div class="alert alert-warning small mt-3 mb-0">
        Se a opcao nao aparecer no WhatsApp, toque em <strong>Abrir no Safari</strong> e repita.
      </div>` : ''}`

  const android_steps = `
    <div class="small text-muted mb-3">No Android, o atalho abre com o contexto atual do app.</div>
    <ol class="ps-3 mb-0">
      <li class="mb-2">Abra o menu do navegador.</li>
      <li class="mb-2">Toque em <strong>Instalar app</strong> ou <strong>Adicionar a tela inicial</strong>.</li>
      <li>Confirme a instalacao do atalho.</li>
    </ol>`

  const content = `
    <div class="p-3">
      <div class="d-flex align-items-center gap-2 mb-3">
        <span class="rounded-circle bg-primary bg-opacity-10 text-primary d-inline-flex align-items-center justify-content-center" style="width:40px;height:40px">
          <i class="fas fa-mobile-screen-button"></i>
        </span>
        <div>
          <div class="fw-semibold">Criar atalho</div>
          <div class="small text-muted">O app reabre com o posto e filtros atuais.</div>
        </div>
      </div>
      ${is_ios_mobile() ? ios_steps : android_steps}
    </div>`

  modal_html(content, 'Criar atalho', 420, 420, { modal_id: 'install_help_modal', size: 'sm' })
}

async function open_install_flow(): Promise<void> {
  if (banner_kind() === 'native-app') {
    const store_link = preferred_native_app_store()
    if (store_link) window.location.assign(store_link.url)
    return
  }

  const install_state = await prepare_pwa_shortcut_install({
    reload_document: has_manual_install_flow() && !deferred_prompt,
  })
  if (install_state === 'reloading') return

  if (deferred_prompt) {
    void deferred_prompt.prompt()
    const { outcome } = await deferred_prompt.userChoice
    if (outcome === 'accepted') remove_banner()
    else restore_pwa_shortcut_install_url()
    deferred_prompt = null
    return
  }

  show_install_help()
}

function show_banner(): void {
  if (was_dismissed() || !can_offer_install_shortcut()) return

  const kind = banner_kind()
  const copy = banner_copy(kind)
  const brand = banner_brand()
  const safe_brand_name = escapeHtml(brand.name)
  const store_link = preferred_native_app_store()
  const store_links = visible_native_app_stores()
  const actions = kind === 'native-app' && store_link
    ? app_store_link_html(store_link, true)
    : `${store_links.map(link => app_store_link_html(link)).join('')}<button id="install-btn" class="btn btn-primary btn-sm">${copy.button}</button>`

  if (!banner_el) {
    banner_el = document.createElement('div')
    banner_el.id = 'install-banner'
    document.body.appendChild(banner_el)
  }
  banner_el.innerHTML = `
    <div class="install-banner-content install-banner-content--${kind}">
      <div class="d-flex align-items-center gap-3">
        ${banner_brand_media(brand)}
        <div class="install-banner-copy flex-grow-1">
          <div class="install-banner-brand-name fw-semibold small">${safe_brand_name} no aparelho</div>
          <div class="text-muted" style="font-size:0.75rem">${copy.subtitle}</div>
        </div>
        <div class="install-banner-actions">${actions}</div>
        <button id="install-dismiss" class="btn btn-link btn-sm text-muted px-1" aria-label="Fechar">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>`

  bind_banner_events()
}

function remove_banner(immediate = false): void {
  if (banner_el) {
    if (immediate) {
      banner_el.remove()
      banner_el = null
      return
    }
    banner_el.classList.add('install-banner-hide')
    setTimeout(() => { banner_el?.remove(); banner_el = null }, 300)
  }
}

export function can_offer_install_shortcut(): boolean {
  if (should_defer_to_ios_smart_app_banner(
    navigator.userAgent || '',
    window.location.pathname,
    window.location.search,
    is_rede_mode(),
  )) return false

  const kind = banner_kind()
  return can_offer_installation({
    standalone: is_standalone(),
    has_install_session: has_install_session(),
    native_app_preferred: kind === 'native-app',
    native_store_available: !!preferred_native_app_store(),
    pwa_prompt_available: !!deferred_prompt,
    manual_install_available: has_manual_install_flow() && !is_pwa_install_blocked(),
  })
}

export function sync_install_banner(): void {
  if (can_offer_install_shortcut()) {
    show_banner()
    return
  }
  remove_banner(true)
}

export async function open_install_cta(): Promise<void> {
  if (!can_offer_install_shortcut()) return
  await open_install_flow()
}

export function init_install_banner(): void {
  if (is_standalone()) return

  load_app_store_links()
  sync_install_banner()

  store.on('phone', () => sync_install_banner())
  store.on('driver_phone', () => sync_install_banner())
  store.on('station_name', () => sync_install_banner())
  store.on('station_logo', () => sync_install_banner())
  store.on('rede_mode', () => sync_install_banner())
  store.on('rede_nome', () => sync_install_banner())
  store.on('rede_logo', () => sync_install_banner())

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    if (is_pwa_install_blocked()) {
      deferred_prompt = null
      remove_banner(true)
      sync_install_banner()
      return
    }
    deferred_prompt = e as BeforeInstallPromptEvent
    remove_banner(true)
    sync_install_banner()
  })

  window.addEventListener('appinstalled', () => {
    restore_pwa_shortcut_install_url()
    remove_banner()
    deferred_prompt = null
  })

  if (consume_pending_pwa_shortcut_install_help()) {
    window.setTimeout(() => {
      if (!can_offer_install_shortcut() || deferred_prompt || is_pwa_install_blocked()) return
      show_install_help()
    }, 250)
  }
}
